'use client';

import {
  useCatalogContext,
  useRequireCiC,
} from '@dealerportal/catalog-ui/Contexts/CatalogContext';
import {
  type DoorStyleDetails as DoorStyleDetailsData,
  doorStyleCode,
  doorStyleHref,
  doorStyleSku,
  type ProductInformation,
  type Swatch,
} from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
import { useClientContext } from '@dealerportal/react-helpers';
import { useRouter } from 'next/navigation';
import { type ReactNode, useEffect, useMemo } from 'react';
import { Configurator } from '../Configurator/Configurator';
import {
  DOOR_FEATURE_CODE,
  FINISH_FEATURE_CODE,
} from '../Configurator/featureHelpers';
import { useItemConfiguration } from '../Configurator/useItemConfiguration';
import { useItemModel } from '../Configurator/useItemModel';
import { DoorStyleCard } from '../DoorStyleSrp/DoorStyleCard';
import { KbfProductMedia } from '../ProductDetails/KbfProductMedia';
import { ProductDetailsCtas } from '../ProductDetails/ProductDetailsCtas';
import { ProductDetailsHeader } from '../ProductDetails/ProductDetailsHeader';
import { ProductDetailsLayout } from '../ProductDetails/ProductDetailsLayout';
import { ProductDetailsRequestForm } from '../ProductDetails/ProductDetailsRequestForm';
import { ProductDetailsTabs } from '../ProductDetails/ProductDetailsTabs';
import { ProductDetailsTopBar } from '../ProductDetails/ProductDetailsTopBar';
import { ProductSpec } from '../ProductDetails/ProductSpec';
import { Section } from '../ProductDetails/Section';
import { SwatchCollection } from '../ProductDetails/SwatchCollection';
import { useScrollToHash } from '../ProductDetails/useScrollToHash';
import { useDisplayItem } from './useDisplayItem';

export type DoorStyleDetailsProps = {
  details: DoorStyleDetailsData;
  isMobileDevice: boolean;
  /**
   * Item the door style is rendered on in 3D (site setting); defaults to a
   * wide double-door base cabinet of the door's catalog.
   */
  displayItemCode?: string;
  /** Dealer-authored HTML from the `PDPContentTopSection` token. */
  topSectionContent?: ReactNode;
  /** The dealer's lead form. */
  actonForm: ReactNode;
  /** Back-link target; defaults to the door-style SRP. */
  backHref?: string;
};

type Labels = {
  brand: string;
  doorStyle: string;
  finish: string;
  species: string;
  sku: string;
};

/**
 * Present a door × finish pairing as the product the shared PDP blocks
 * expect: the door style is the product line (by its catalog's brand), the
 * finish its colour, and the catalog's style item (when it exists) supplies
 * the SKU. The price group stays out: dealers do not show pricing. Exported
 * for tests.
 */
export const doorStyleAsProduct = (
  { catalogVersionId, door, finish, styleItem }: DoorStyleDetailsData,
  labels: Labels
): ProductInformation => {
  // Catalogs without style items have no SKU per finish; the door code is
  // what such a door style is quoted by.
  const sku =
    styleItem?.manufCode ??
    finish.styleCode?.toUpperCase() ??
    doorStyleCode(door);
  const doorStyle = door.styleName
    ? `${door.styleName} (${doorStyleCode(door)})`
    : door.name;
  const swatches: Swatch[] = door.finishes.map((f) => ({
    colorName: f.name,
    imageURL: f.imageUrl ?? '',
    slug: door.code,
    sku: doorStyleSku(f),
    UPID: doorStyleSku(f),
  }));
  return {
    productId: styleItem?.id ?? `${catalogVersionId}.${doorStyleSku(finish)}`,
    UPID: doorStyleSku(finish),
    sku,
    slug: door.code,
    brandName: door.brandName ?? '',
    productName: door.name,
    productType: labels.doorStyle,
    productTypeSlug: 'door-style',
    colorName: finish.name,
    description: styleItem?.description ?? '',
    specs: [
      ...(door.brandName ? [{ key: labels.brand, value: door.brandName }] : []),
      { key: labels.doorStyle, value: doorStyle },
      { key: labels.finish, value: finish.name },
      { key: labels.species, value: finish.species },
      { key: labels.sku, value: sku },
    ],
    swatches,
    unitPrice: 0,
    tags: [],
    files: [],
    features: [],
  };
};

/**
 * Detail page of a door style in a finish — the KB&F "product": the 2D door
 * render as hero, the door's finishes as display-only swatches that grow on
 * hover, specs from the catalog's combination (no pricing or price group),
 * the shared CTAs and lead form, and the configurator: a wide display
 * cabinet in this door style and finish, every other option editable, with
 * the 3D model rendered by CiC for the configuration as picked. Door and
 * finish picks there change the product, so they move to that SKU's page.
 */
export const DoorStyleDetails = ({
  details,
  isMobileDevice,
  displayItemCode,
  topSectionContent,
  actonForm,
  backHref = '/door-styles',
}: DoorStyleDetailsProps) => {
  const t = useTranslations('products');
  const tSpecs = useTranslations('specs');
  const ctx = useClientContext();
  const { loaded } = useCatalogContext();
  useRequireCiC();
  const router = useRouter();
  const { door, finish, styleItem, otherDoors } = details;

  const product = doorStyleAsProduct(details, {
    brand: tSpecs('brand'),
    doorStyle: t('doorStyle'),
    finish: t('doorFinish'),
    species: t('woodSpecies'),
    sku: t('sku').replace(/[#:\s]+$/, ''),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: record the visit once
  useEffect(() => {
    ctx.addRecentProduct(product);
  }, []);

  const display = useDisplayItem({
    catalogVersionId: String(details.catalogVersionId),
    displayItemCode,
    doorCode: door.code,
    finish,
    enabled: loaded,
  });

  // The display item as the configuration hooks expect a product: they only
  // read its id and features. Memoised so a re-render keeps the picks.
  const displayProduct = useMemo(
    () =>
      ({
        productId: display.item?.id ?? '',
        features: display.features,
      }) as ProductInformation,
    [display.item?.id, display.features]
  );
  const configuration = useItemConfiguration(displayProduct);
  const model = useItemModel({
    itemId: display.item?.id ?? '',
    features: configuration.features,
    isResolving: configuration.isResolving,
  });

  const hasConfigurator =
    display.isLoading || configuration.features.length > 0;

  // Order Sample on the SRP lands on `#request-form`; the configurator only
  // mounts once the CiC SDK is up.
  useScrollToHash(['request-form', 'configurator']);

  // Door and finish are this page's product; every other option is edited
  // in place on the display cabinet.
  const handleOptionSelect: typeof configuration.onOptionSelect = (
    feature,
    option
  ) => {
    if (feature.code === FINISH_FEATURE_CODE) {
      const picked = door.finishes.find((f) => f.code === option.code);
      if (picked) {
        router.push(doorStyleHref(door, doorStyleSku(picked)));
        return;
      }
    }
    if (feature.code === DOOR_FEATURE_CODE && option.code !== door.code) {
      // The page settles on that door's own SKU for this finish, or its
      // first finish when the door is not offered in this one. The display
      // cabinet comes from this door's catalog, and so does the picked door.
      router.push(
        doorStyleHref(
          { catalogKey: door.catalogKey, code: option.code },
          finish.code
        )
      );
      return;
    }
    configuration.onOptionSelect(feature, option);
  };

  const renderedFinishName = display.resolution
    ? (door.finishes.find((f) => f.code === display.resolution?.finish)?.name ??
      display.resolution.finish)
    : undefined;

  const header = (
    <ProductDetailsHeader
      productInfo={product}
      isMobileDevice={isMobileDevice}
      tags={[]}
    />
  );

  const aside = (
    <>
      {/* The finishes this door comes in, to look at: a finish is picked in
          the configurator. No pricing section: dealers do not show prices
          for cabinets. */}
      <div className="pdp__swatches">
        <SwatchCollection
          swatches={product.swatches}
          activeSwatch={doorStyleSku(finish)}
          displayOnly
        />
      </div>

      <ProductDetailsCtas
        productInfo={product}
        scrollCta={
          hasConfigurator
            ? {
                id: 'configure-button',
                label: t('configure'),
                targetId: 'configurator',
                primary: true,
              }
            : undefined
        }
        requestInfoPrimary={!hasConfigurator}
      />
    </>
  );

  return (
    <ProductDetailsLayout
      isMobileDevice={isMobileDevice}
      topBar={
        <ProductDetailsTopBar
          backHref={backHref}
          backLabel={t('viewAllDoorStyles')}
        />
      }
      media={
        // The 2D door render, not a second 3D view: the configurator below
        // already shows the model.
        <KbfProductMedia
          productInfo={product}
          heroImage={styleItem?.imageUrl ?? door.imageUrl}
          model={model}
        />
      }
      header={header}
      aside={aside}
      topSectionContent={topSectionContent}
      footer={
        <ProductDetailsRequestForm>{actonForm}</ProductDetailsRequestForm>
      }
    >
      {hasConfigurator && (
        <Section
          id="configurator"
          title={t('configure')}
          description={t('configureDescription')}
          className="pdp__configurator ui-text-left"
        >
          <Configurator
            itemId={display.item?.id ?? ''}
            features={configuration.features}
            onOptionSelect={handleOptionSelect}
            modelSrc={model.src}
            modelSizeBytes={model.sizeBytes}
            modelFileName={`${door.code}-${doorStyleSku(finish)}.glb`}
            modelAlt={`3D model of ${door.name} in ${finish.name}`}
            modelLoading={display.isLoading || model.isLoading}
            disabled={display.isLoading || configuration.isResolving}
          />
          {display.item && (
            <p className="ui-m-0 ui-mt-3 ui-text-sm ui-text-gray-500">
              {t('shownOn', { item: display.item.name })}
            </p>
          )}
          {display.resolution && !display.resolution.exact && (
            <p
              role="note"
              className="ui-m-0 ui-mt-1 ui-text-sm ui-text-amber-700"
            >
              {t('previewFinishNotice', { finish: renderedFinishName ?? '' })}
            </p>
          )}
        </Section>
      )}

      {/* Specifications only: the style item's description is a coded label
          ("330S Map Cider G1") that the specs already spell out. */}
      <ProductDetailsTabs
        className="pdp__specs"
        tabs={[
          {
            id: 'specifications',
            label: t('specifications'),
            content: <ProductSpec specs={product.specs} />,
          },
        ]}
      />

      {otherDoors.length > 0 && (
        <Section
          title={t('otherDoorStyles')}
          className="pdp__similar ui-text-left"
        >
          <div className="ui-grid md:ui-grid-cols-2 lg:ui-grid-cols-4 ui-gap-4">
            {otherDoors.map((card) => (
              <DoorStyleCard
                key={`${card.catalogKey}/${card.code}`}
                card={card}
                href={(picked) => {
                  const target = picked ?? card.finishes[0];
                  return target
                    ? doorStyleHref(card, doorStyleSku(target))
                    : undefined;
                }}
              />
            ))}
          </div>
        </Section>
      )}
    </ProductDetailsLayout>
  );
};
